import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import Seo from "../components/Seo";
import api, { apiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { formatINR, FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from "../data/products";

const FIELDS = [
  { name: "name", label: "Full Name", type: "text", full: true, placeholder: "Aanya Sharma" },
  { name: "email", label: "Email", type: "email", placeholder: "you@example.com" },
  { name: "phone", label: "Phone", type: "tel", placeholder: "+91 98765 43210" },
  { name: "address", label: "Address", type: "text", full: true, placeholder: "Flat, street, landmark" },
  { name: "city", label: "City", type: "text", placeholder: "Mumbai" },
  { name: "state", label: "State", type: "text", placeholder: "Maharashtra" },
  { name: "pin", label: "PIN Code", type: "text", placeholder: "400001" },
];

const PAYMENTS = [
  { id: "card", label: "Credit / Debit Card" },
  { id: "upi", label: "UPI" },
];

const inputCls =
  "w-full border-b border-cocoa/25 bg-transparent py-3 text-sm font-light outline-none transition-colors placeholder:text-taupe/50 focus:border-gold";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-taupe";

export default function Checkout() {
  const { detailed, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", pin: "" });
  const [payment, setPayment] = useState("card");
  const [placing, setPlacing] = useState(false);

  const shipping = subtotal === 0 || subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const total = subtotal + shipping;

  if (detailed.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 bg-ivory px-6 text-center" data-testid="checkout-empty">
        <Seo title="Checkout — Zelvoria" siteName="ZELVORIA" description="Complete your Zelvoria order." />
        <h1 className="font-serif text-4xl font-light text-ink">Your bag is empty.</h1>
        <Link
          to="/candles"
          data-testid="checkout-empty-shop-button"
          className="bg-cocoa px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors hover:bg-gold"
        >
          Shop Candles
        </Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        customer: form,
        items: detailed.map(({ slug, qty, product }) => ({
          slug,
          name: product.name,
          price: product.price,
          qty,
          image: product.image,
        })),
        payment_method: payment,
      });
      clear();
      navigate("/order-success", { state: { order: data } });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="checkout-page">
      <Seo title="Checkout — Zelvoria" siteName="ZELVORIA" description="Complete your Zelvoria order." />
      <form onSubmit={placeOrder} className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
        <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Almost There</p>
        <h1 className="mt-3 font-serif text-4xl font-light text-ink md:text-5xl">Checkout</h1>

        <div className="mt-12 grid gap-14 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="font-serif text-2xl font-light text-ink">Delivery Details</h2>
            <div className="mt-8 grid gap-x-8 gap-y-8 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                  <label htmlFor={`checkout-${f.name}`} className={labelCls}>{f.label}</label>
                  <input
                    id={`checkout-${f.name}`}
                    type={f.type}
                    required
                    value={form[f.name]}
                    onChange={set(f.name)}
                    placeholder={f.placeholder}
                    data-testid={`checkout-${f.name}-input`}
                    className={inputCls}
                  />
                </div>
              ))}
            </div>

            <h2 className="mt-14 font-serif text-2xl font-light text-ink">Payment</h2>
            <div className="mt-6 space-y-3" data-testid="payment-methods">
              {PAYMENTS.map((p) => (
                <label
                  key={p.id}
                  data-testid={`payment-${p.id}`}
                  className={`flex cursor-pointer items-center gap-4 border px-5 py-4 transition-colors duration-300 ${
                    payment === p.id ? "border-gold bg-gold/5" : "border-beige hover:border-gold/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value={p.id}
                    checked={payment === p.id}
                    onChange={() => setPayment(p.id)}
                    className="accent-[#A8752F]"
                  />
                  <span className="text-sm text-ink">{p.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-4 flex items-center gap-2 text-xs font-light text-taupe">
              <Lock size={12} className="text-gold" />
              Demonstration checkout — no payment is charged. A live Razorpay / Stripe gateway can be enabled here.
            </p>
          </div>

          <aside className="h-fit border border-beige bg-parchment p-8 lg:sticky lg:top-32 lg:col-span-2" data-testid="checkout-summary">
            <h2 className="font-serif text-2xl font-light text-ink">Order Summary</h2>
            <div className="mt-6 space-y-4">
              {detailed.map(({ slug, qty, product }) => (
                <div key={slug} className="flex items-center gap-4" data-testid={`checkout-item-${slug}`}>
                  <span className="h-14 w-12 shrink-0 overflow-hidden bg-cream">
                    <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
                  </span>
                  <span className="flex-1">
                    <span className="block font-serif text-base text-ink">{product.name}</span>
                    <span className="text-xs text-taupe">Qty {qty}</span>
                  </span>
                  <span className="text-sm text-ink">{formatINR(product.price * qty)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-3 border-t border-beige pt-5 text-sm">
              <div className="flex justify-between">
                <span className="text-taupe">Subtotal</span>
                <span className="text-ink" data-testid="checkout-subtotal">{formatINR(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-taupe">Shipping</span>
                <span className="text-ink">{shipping === 0 ? "Complimentary" : formatINR(shipping)}</span>
              </div>
              <div className="flex justify-between border-t border-beige pt-4 text-base">
                <span className="uppercase tracking-[0.15em] text-ink">Total</span>
                <span className="font-medium text-ink" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={placing}
              data-testid="place-order-button"
              className="mt-8 w-full bg-cocoa py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold disabled:opacity-60"
            >
              {placing ? "Placing Order…" : "Place Order"}
            </button>
          </aside>
        </div>
      </form>
    </div>
  );
}
